import { Scheme } from 'app/pages/Payment/data';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useModals from '../Modal/manager/useModal';
import {
  checkEmail,
  convertInstallmentStringToOptions,
  findImage,
  isPaymentTokenExpired,
} from 'utils/Utils';
import ReactSelect from 'react-select';
import { usePaymentState } from 'payment_state/PaymentStateContext';
import { useMutation } from 'react-query';
import { innerSearch } from 'api/Api';
import { RequestPVKRForm } from './types';
import TermsModal from '../Modal/TermsModal';
import PrivacyPolicyModal from '../Modal/PrivacyPolicyModal';
import CustomModal from '../Modal/CustomModal';
import ErrorModal from '../Modal/ErrorModal';
import { isMobile } from 'react-device-detect';
import ArrowPng from 'app/assets/image/common/arrow.png';
import { useMediaQuery } from 'react-responsive';

interface CreditKoreaProps {
  data: Scheme[];
  selectedScheme: Scheme;
  setSelectedScheme: (method: Scheme) => void;
  previous: (e: any) => void;
  setLoading: (method: boolean) => void;
  setPopup: (e: Window | null) => void;
  isUnique: boolean;
  handleClosed: (e) => void;
  isGlobal: boolean;
}

type Option = { label: string; value: string };

const CreditKoreaForm = ({
  data,
  selectedScheme,
  setSelectedScheme,
  previous,
  setLoading,
  setPopup,
  isUnique,
  handleClosed,
  isGlobal,
}: CreditKoreaProps) => {
  const { t, i18n } = useTranslation();
  const monthOptions = [
    { label: t('installment-00'), value: '00' },
    { label: t('installment-02'), value: '02' },
    { label: t('installment-03'), value: '03' },
    { label: t('installment-04'), value: '04' },
    { label: t('installment-05'), value: '05' },
    { label: t('installment-06'), value: '06' },
    { label: t('installment-07'), value: '07' },
    { label: t('installment-08'), value: '08' },
    { label: t('installment-09'), value: '09' },
    { label: t('installment-10'), value: '10' },
    { label: t('installment-11'), value: '11' },
    { label: t('installment-12'), value: '12' },
  ];
  let checkPopupTimer;
  const foucsTerms = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const { openModal } = useModals();
  const { paymentState, setPaymentState, setShopifyState } = usePaymentState();
  const [isDisabledInstallmentForm, setDisabledInstallmentForm] =
    useState<boolean>(true);
  const [installmentMonth, setInstallmentMonth] = useState<string>('00');
  const [installmentMonthOptions, setInstallmentMonthOptions] = useState<
    Option[]
  >([]);
  const [selectedOption, setSelectedOption] = useState<{
    label: string;
    value: string;
  }>(monthOptions[0]);
  const [email, setEmail] = useState<string>('');
  const [agreeCheck, setAgreeCheck] = useState<boolean>(false);
  const [AgreeCheckError, setAgreeCheckError] = useState<boolean>(false);
  const [pvkrRequestBody, setPvkrRequestBody] = useState<RequestPVKRForm>();
  const [open, setOpen] = useState<boolean>(false);
  const isMobileSize = useMediaQuery({ maxWidth: 767 });

  useEffect(() => {
    setSelectedOption(installmentMonthOptions[0]);
  }, [installmentMonthOptions]);

  useEffect(() => {
    setAgreeCheckError(false);
  }, [agreeCheck]);

  useEffect(() => {
    if (selectedScheme.installmentList) {
      setInstallmentMonthOptions(
        convertInstallmentStringToOptions(selectedScheme.installmentList, t),
      );
    } else {
      setInstallmentMonthOptions(monthOptions);
    }
  }, [selectedScheme, i18n.language]);

  // InnerSearch 실행
  const transactResult = useMutation(() => innerSearch(paymentState.tid), {
    onSuccess: data => {
      setLoading(false);
      if (data.data.returnUrl.includes('shopifyConfirm')) {
        setShopifyState(data.data.response);
        navigate('/shopifyConfirm');
      } else {
        // InnerSearch 결과처리
        payResult(data);

        // 결과 상태가 없을경우 예외처리
        if (
          data.data.response.resultStatus === null ||
          data.data.response.resultStatus === undefined
        ) {
          navigate('/error');
        }
      }
    },
    onError: error => {
      setLoading(false);
      navigate('/error');
    },
  });

  const payResult = data => {
    // 결과 상태
    const resultStatus = String(data.data.response.resultStatus).toLowerCase();
    // 결제 타입
    const payType = String(data.data.display.payType).toLowerCase();

    // InnerSearch 결과 값 PaymentState 설정
    setPaymentState(prevState => ({
      ...prevState,
      resultCode: data.data.response.resultCode,
      resultMessage: data.data.response.resultMessage,
      tid: data.data.response.tid,
      orderId: data.data.response.orderId,
      mallReserved: data.data.response.mallReserved,
      // returnUrl: data.data.returnUrl,
      descriptor: data.data.display.descriptor,
      schemeGroup: data.data.response.schemeGroup,
      schemeCode: data.data.response.schemeCode,
    }));

    // Status에 따른 Paging 처리
    switch (resultStatus) {
      case 'success':
        navigate('/success');
        break;
      case 'decline':
        navigate('/decline');
        break;
      case 'processing':
        navigate('/processing');
        break;
      case 'pending':
        payType === 'vbank' || payType === 'otc'
          ? navigate('/vbankPending')
          : navigate('/pending');
        break;
      case 'failed':
        navigate('/failed');
        break;
      default:
        navigate('/error');
    }
  };

  const handleTermsOrPrivacy = content => {
    if (content === 'SERVICE') {
      openModal(TermsModal, {
        onSubmit: () => {},
      });
    } else {
      openModal(PrivacyPolicyModal, {
        onSubmit: () => {},
      });
    }
  };

  // 결제카드 선택
  const selectSchemeAction = (scheme: Scheme) => {
    const installMinAmt = scheme.installmentMinAmt ?? 50000;
    if (scheme.approvalAmount >= installMinAmt) {
      setDisabledInstallmentForm(false);
    }
    setSelectedScheme(scheme);
  };

  const handlePayKoreaCard = e => {
    e.preventDefault();

    if (selectedScheme.schemeCd === '') {
      // 카드를 선택하지 않은 경우
      openModal(ErrorModal, {
        message: t('error_card_select'),
      });
      return;
    }

    if (email !== '') {
      if (!checkEmail(email)) {
        openModal(ErrorModal, {
          message: t('check_email_format'),
        });
        return;
      }
    }

    if (!agreeCheck) {
      setAgreeCheckError(true);
      openModal(ErrorModal, {
        message: t('error_agree_check'),
      });
      foucsTerms.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    setAgreeCheck(false);

    if (isPaymentTokenExpired()) {
      // token expired
      navigate('/paymentExpired');

      return;
    }

    // 결제 Body 생성
    const pvkrBody: RequestPVKRForm = {
      schemeCode: selectedScheme.schemeCd,
      authorization: `Bearer ${paymentState.auth}`,
      amount: paymentState.requestAmount,
      clientKey: paymentState.clientKey,
      currency: paymentState.requestCurrency,
      mid: paymentState.mid,
      orderId: paymentState.orderId,
      productName: paymentState.productName,
      reqDate: paymentState.reqDate,
      webhookUrl: paymentState.webhookUrl,
      returnUrl: paymentState.returnUrl,
      sign: paymentState.sign,
      tid: paymentState.tid,
      email: email,
      displayType: 'UI',
      mallReserved: paymentState.mallReserved,
      installmentMonths: installmentMonth,
    };
    setPvkrRequestBody(pvkrBody);
    const name = t(selectedScheme.schemeDisplayNm.toLowerCase());
    openModal(CustomModal, {
      message: t('open_card_app', { name: name }),
      isShowTwoButton: true,
      onSubmit: async () => {
        setLoading(true);
        if (isMobile) {
          navigate('/formSubmit?schemeGroup=PVKR', {
            state: { formData: pvkrBody },
          });
        } else {
          const popup = window.open(
            '/formSubmit?schemeGroup=PVKR',
            '_blank',
            'width=700, height=850',
          );
          setPopup(popup);
          if (popup) {
            setTimeout(() => {
              popup?.postMessage(
                { eventType: 'formData', data: pvkrBody },
                window.location.origin,
              );
            }, 1000);
          }
          checkPopupTimer = setInterval(checkPopupClosed, 1000, popup);
        }
      },
    });
  };

  function checkPopupClosed(popup) {
    if (popup && popup.closed) {
      clearInterval(checkPopupTimer);
      const ErrorKey = window.sessionStorage.getItem('isError');
      if (ErrorKey !== 'true') {
        transactResult.mutate();
      }
    }
  }
  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Enter 키의 기본 동작을 중지시킴
    }
  };

  return (
    <>
      <div className="tab">
        <div
          id="tab-1"
          className="tab-on current"
          style={{
            height: isGlobal
              ? isMobileSize
                ? 'calc((var(--vh, 1vh) * 100) - 414px)'
                : 184
              : isMobileSize
              ? 'calc((var(--vh, 1vh) * 100) - 348px)'
              : 250,
          }}
        >
          <div className="card">
            {data &&
              data.map((scheme, index) => {
                return (
                  <React.Fragment key={index}>
                    <input
                      type="radio"
                      id="sort-kr-card"
                      checked={selectedScheme === scheme}
                      readOnly
                    />
                    <label
                      htmlFor="sort-kr-card"
                      onClick={() => {
                        selectSchemeAction(scheme);
                      }}
                    >
                      <div className="middle">
                        <div className="img">
                          <img
                            src={findImage(`kr-card/${scheme.schemeCd}.png`)}
                          />
                        </div>
                      </div>
                    </label>
                  </React.Fragment>
                );
              })}
          </div>
          {data && selectedScheme.installmentAvailable === 'Y' && (
            <div className="installment-form">
              <div className="top">
                <div className="left">{t('installment-title')}</div>
                <div className="right">
                  <ul className="installment-depth">
                    <li>
                      <div className={true ? 'input error' : 'input'}>
                        <ReactSelect
                          className="user-info-select-country"
                          classNamePrefix="user-info-select-country_"
                          options={installmentMonthOptions}
                          placeholder={t('installment-00')}
                          onChange={e => {
                            if (e) {
                              setInstallmentMonth(e.value);
                              setSelectedOption(e);
                            }
                          }}
                          value={selectedOption}
                          isDisabled={isDisabledInstallmentForm}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: base => ({
                              ...base,
                              zIndex: 9999,
                            }),
                            input: base => ({
                              ...base,
                              width: 0,
                              opacity: 0,
                            }),
                          }}
                          isSearchable={false}
                        ></ReactSelect>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="down">
                <div className="left"></div>
                <div className="right">
                  <div className="left">*</div>
                  <div
                    className="right"
                    dangerouslySetInnerHTML={{
                      __html: t('installment-content'),
                    }}
                  ></div>
                </div>
              </div>
            </div>
          )}
          <div className="emailOptionBox">
            <div className="title">{t('payment_confirm_email_opt')}</div>
            <div className="input">
              <input
                name="email"
                type="email"
                onChange={e => setEmail(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="agreeFloatBox">
          <div className="label" ref={foucsTerms}>
            <div className="check">
              <input
                type="checkbox"
                id={AgreeCheckError ? 'checkbox error' : 'checkbox'}
                name="agree"
                checked={agreeCheck}
                onChange={e => {
                  setAgreeCheck(e.target.checked);
                }}
              />

              <label
                htmlFor={AgreeCheckError ? 'checkbox error' : 'checkbox'}
              ></label>
            </div>
            <div className="columnBox">
              <div className="text">
                <div
                  onClick={() => setAgreeCheck(!agreeCheck)}
                  style={{ cursor: 'pointer' }}
                >
                  {t('start_agree_terms')}
                  {t('terms_and_conditions')}
                  {t('and_the')}
                  {t('privacy_policy')}
                  {t('end_agree_terms')}
                </div>
                <img
                  className={`arrow ${open ? 'open' : ''}`}
                  src={ArrowPng}
                  alt=""
                  onClick={() => setOpen(!open)}
                />
              </div>
              <div className={`text toggleRow ${open ? 'open' : ''}`}>
                <p className="dot">●</p>
                <span
                  className="link"
                  onClick={() => handleTermsOrPrivacy('SERVICE')}
                >
                  {t('terms_and_conditions')}
                </span>
              </div>
              <div className={`text toggleRow ${open ? 'open' : ''}`}>
                <p className="dot">●</p>
                <span
                  className="link"
                  onClick={() => handleTermsOrPrivacy('PRIVACY')}
                >
                  {t('privacy_policy')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="c-section">
        <div className="box">
          {isUnique ? (
            <button className="all-close" onClick={handleClosed}>
              {t('cancel')}
            </button>
          ) : (
            <button className="all-close" onClick={previous}>
              {t('previous')}
            </button>
          )}
          <button
            className="orange"
            onClick={handlePayKoreaCard}
            onKeyDown={e => handleKeyDown(e)}
          >
            {t('pay_now')}
          </button>
        </div>
      </div>
    </>
  );
};

export default CreditKoreaForm;
